import { parseMoney } from './money';
import type { CreateProductsInput, SizeUnit } from './vendorApiClient';

/**
 * The "Add a product" form: what the product is, entered once, then a row per
 * size. Held as the text the person typed until they send it; `checkAddForm`
 * turns that into what the vendor-api takes, or says what to fix and where.
 */

export const SIZE_UNITS: SizeUnit[] = ['oz', 'lb', 'g', 'kg', 'fl oz', 'ml', 'l', 'pt', 'qt', 'gal', 'ct'];

/** '' = not answered yet. "I don't have one" is an answer; a blank is not. */
export type AttachmentChoice = '' | 'upload' | 'none';

export interface SizeRowForm {
  /** Stable for the life of the row, so a row keeps its messages when another is removed. */
  key: string;
  sizeValue: string;
  sizeUnit: SizeUnit;
  noBarcode: boolean;
  barcode: string;
  vendorSku: string;
  price: string;
  allLocations: boolean;
  storeIds: string[];
}

export interface AddProductFormState {
  name: string;
  brand: string;
  department: string;
  category: string;
  descriptionShort: string;
  descriptionLong: string;
  ingredients: string;
  photo: AttachmentChoice;
  photoUrl: string | null;
  nutritionLabel: AttachmentChoice;
  nutritionLabelUrl: string | null;
  servings: string;
  itemNameEs: string;
  descriptionShortEs: string;
  sizes: SizeRowForm[];
}

export type FamilyField = 'name' | 'department' | 'category' | 'descriptionShort' | 'ingredients' | 'photo' | 'nutritionLabel' | 'servings';
export type SizeField = 'size' | 'barcode' | 'price' | 'locations';

export type FamilyProblem =
  | 'name_required'
  | 'department_required'
  | 'category_required'
  | 'description_required'
  | 'ingredients_required'
  | 'photo_required'
  | 'photo_missing'
  | 'label_required'
  | 'label_missing'
  | 'servings_invalid';

export type SizeProblem =
  | 'size_invalid'
  | 'duplicate_size'
  | 'barcode_required'
  | 'barcode_invalid'
  | 'barcode_in_store'
  | 'duplicate_barcode'
  | 'price_required'
  | 'price_invalid'
  | 'locations_required';

export interface AddFormProblems {
  family: Partial<Record<FamilyField, FamilyProblem>>;
  sizes: Record<string, Partial<Record<SizeField, SizeProblem>>>;
}

let rowSeq = 0;
export function emptySize(unit: SizeUnit = 'lb'): SizeRowForm {
  rowSeq += 1;
  return { key: `size-${rowSeq}`, sizeValue: '', sizeUnit: unit, noBarcode: false, barcode: '', vendorSku: '', price: '', allLocations: true, storeIds: [] };
}

export function emptyForm(): AddProductFormState {
  return {
    name: '',
    brand: '',
    department: '',
    category: '',
    descriptionShort: '',
    descriptionLong: '',
    ingredients: '',
    photo: '',
    photoUrl: null,
    nutritionLabel: '',
    nutritionLabelUrl: null,
    servings: '',
    itemNameEs: '',
    descriptionShortEs: '',
    sizes: [emptySize()],
  };
}

function upcaCheckDigit(first11: string): string {
  let sum = 0;
  for (let i = 0; i < 11; i++) sum += Number(first11[i]) * (i % 2 === 0 ? 3 : 1);
  return String((10 - (sum % 10)) % 10);
}

function ean13CheckDigit(first12: string): string {
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(first12[i]) * (i % 2 === 0 ? 1 : 3);
  return String((10 - (sum % 10)) % 10);
}

export type BarcodeCheck = { ok: true; code: string } | { ok: false; problem: 'barcode_invalid' | 'barcode_in_store' };

/**
 * The same checks the server makes, so a mistyped digit is caught as it is
 * typed rather than after sending. 12 or 13 digits, a correct check digit, and
 * not one of the ranges a store prints for itself (they are not unique between
 * stores, so they cannot identify a product).
 */
export function checkBarcode(input: string): BarcodeCheck {
  const digits = input.replace(/[\s-]/g, '');
  if (!/^\d{12,13}$/.test(digits)) return { ok: false, problem: 'barcode_invalid' };
  const code = digits.length === 13 && digits.startsWith('0') ? digits.slice(1) : digits;
  const valid = code.length === 12 ? upcaCheckDigit(code.slice(0, 11)) === code[11] : ean13CheckDigit(code.slice(0, 12)) === code[12];
  if (!valid) return { ok: false, problem: 'barcode_invalid' };
  if (code.length === 12 ? /^[24]/.test(code) : /^2/.test(code)) return { ok: false, problem: 'barcode_in_store' };
  return { ok: true, code };
}

/** "1", "0.5", "12" — up to three decimals, above zero. null when it is not a size. */
export function parseSizeValue(input: string): number | null {
  const text = input.trim();
  if (!/^\d{1,6}(\.\d{1,3})?$/.test(text)) return null;
  const n = Number(text);
  return n > 0 && n <= 100_000 ? n : null;
}

/** Ingredients are required for anything with no barcode: the vendor makes or packs it. */
export function ingredientsRequired(form: AddProductFormState): boolean {
  return form.sizes.some((s) => s.noBarcode);
}

export type AddFormCheck = { ok: true; input: CreateProductsInput } | { ok: false; problems: AddFormProblems };

export function checkAddForm(form: AddProductFormState, sizeOfVendorProductId?: string): AddFormCheck {
  const family: AddFormProblems['family'] = {};
  const sizes: AddFormProblems['sizes'] = {};

  if (!form.name.trim()) family.name = 'name_required';
  if (!form.department) family.department = 'department_required';
  if (!form.category) family.category = 'category_required';
  if (!form.descriptionShort.trim()) family.descriptionShort = 'description_required';
  if (ingredientsRequired(form) && !form.ingredients.trim()) family.ingredients = 'ingredients_required';
  if (!form.photo) family.photo = 'photo_required';
  else if (form.photo === 'upload' && !form.photoUrl) family.photo = 'photo_missing';
  if (!form.nutritionLabel) family.nutritionLabel = 'label_required';
  else if (form.nutritionLabel === 'upload' && !form.nutritionLabelUrl) family.nutritionLabel = 'label_missing';

  let servings: number | undefined;
  if (form.servings.trim()) {
    const n = Number(form.servings.trim());
    if (!/^\d{1,4}(\.\d{1,2})?$/.test(form.servings.trim()) || n <= 0) family.servings = 'servings_invalid';
    else servings = n;
  }

  const out: CreateProductsInput['sizes'] = [];
  const seenSizes = new Set<string>();
  const seenBarcodes = new Set<string>();
  for (const row of form.sizes) {
    const problems: Partial<Record<SizeField, SizeProblem>> = {};

    const value = parseSizeValue(row.sizeValue);
    if (value == null) problems.size = 'size_invalid';
    else {
      const key = `${value}|${row.sizeUnit}`;
      if (seenSizes.has(key)) problems.size = 'duplicate_size';
      seenSizes.add(key);
    }

    let barcode: CreateProductsInput['sizes'][number]['barcode'] = { kind: 'none' };
    if (!row.noBarcode) {
      if (!row.barcode.trim()) problems.barcode = 'barcode_required';
      else {
        const checked = checkBarcode(row.barcode);
        if (!checked.ok) problems.barcode = checked.problem;
        else if (seenBarcodes.has(checked.code)) problems.barcode = 'duplicate_barcode';
        else {
          seenBarcodes.add(checked.code);
          barcode = { kind: 'value', value: checked.code };
        }
      }
    }

    const price = parseMoney(row.price);
    if (!price.ok) problems.price = 'price_invalid';
    else if (price.cents == null) problems.price = 'price_required';

    if (!row.allLocations && row.storeIds.length === 0) problems.locations = 'locations_required';

    if (Object.keys(problems).length > 0) {
      sizes[row.key] = problems;
      continue;
    }
    out.push({
      sizeValue: value as number,
      sizeUnit: row.sizeUnit,
      barcode,
      ...(row.vendorSku.trim() ? { vendorSku: row.vendorSku.trim() } : {}),
      priceCents: (price as { cents: number }).cents,
      locations: row.allLocations ? 'all' : row.storeIds,
    });
  }

  if (Object.keys(family).length > 0 || Object.keys(sizes).length > 0) return { ok: false, problems: { family, sizes } };

  const spanish = {
    ...(form.itemNameEs.trim() ? { itemNameEs: form.itemNameEs.trim() } : {}),
    ...(form.descriptionShortEs.trim() ? { descriptionShortEs: form.descriptionShortEs.trim() } : {}),
  };
  return {
    ok: true,
    input: {
      name: form.name.trim(),
      ...(form.brand.trim() ? { brand: form.brand.trim().slice(0, 100) } : {}),
      department: form.department,
      category: form.category,
      descriptionShort: form.descriptionShort.trim(),
      ...(form.descriptionLong.trim() ? { descriptionLong: form.descriptionLong.trim() } : {}),
      ...(form.ingredients.trim() ? { ingredients: form.ingredients.trim() } : {}),
      photo: form.photo === 'upload' ? { kind: 'upload', url: form.photoUrl as string } : { kind: 'none' },
      nutritionLabel: form.nutritionLabel === 'upload' ? { kind: 'upload', url: form.nutritionLabelUrl as string } : { kind: 'none' },
      ...(servings !== undefined ? { servingsPerContainer: servings } : {}),
      ...(Object.keys(spanish).length > 0 ? { spanish } : {}),
      sizes: out,
      ...(sizeOfVendorProductId ? { sizeOfVendorProductId } : {}),
    },
  };
}

/**
 * Where a problem the SERVER found belongs on the form. The server names the
 * size row by its position; anything it does not place is shown at the top.
 */
const SERVER_FAMILY: Record<string, FamilyField> = {
  name_required: 'name',
  department_required: 'department',
  department_unknown: 'department',
  category_required: 'category',
  category_unknown: 'category',
  description_required: 'descriptionShort',
  ingredients_required: 'ingredients',
  attachment_not_ours: 'photo',
};
const SERVER_SIZE: Record<string, SizeField> = {
  barcode_invalid: 'barcode',
  barcode_in_store: 'barcode',
  duplicate_barcode: 'barcode',
  location_not_found: 'locations',
  size_invalid: 'size',
  duplicate_size: 'size',
};

export function placeServerProblem(
  form: AddProductFormState,
  code: string | undefined,
  sizeIndex: number | undefined,
): { family?: FamilyField; size?: { key: string; field: SizeField } } {
  if (!code) return {};
  if (sizeIndex != null && SERVER_SIZE[code] && form.sizes[sizeIndex]) {
    return { size: { key: form.sizes[sizeIndex].key, field: SERVER_SIZE[code] } };
  }
  return SERVER_FAMILY[code] ? { family: SERVER_FAMILY[code] } : {};
}
